import { captureException } from '@sentry/nextjs';
import type { ScriptManager } from 'lib/scriptManager';
import { safeRun } from 'utils/safeRun';

import type { AnalyticsID } from './constants';
import type {
  BeginCheckoutSchema,
  CartUpdateSchema,
  CartUpsellSchema,
  FreegiftProductSchema,
  HcpLinkClickSchema,
  LoginSchema,
  LongevityMonthFollowInstaSchema,
  LongevityMonthPartnershipClickSchema,
  LongevityMonthShareInstaSchema,
  LongevityMonthShareTiktokSchema,
  LongevityMonthViewSchema,
  OverlayPopupClickSchema,
  PageViewSchema,
  PopupBannerSchema,
  QuizAnswerSchema,
  QuizResultsATCSchema,
  QuizResultsSchema,
  SearchClickSchema,
  SearchQuerySchema,
  SelectItemSchema,
  SelectPromotionSchema,
  SkincareCardClickSchema,
  SkincareCardDisplaySchema,
  VideoMilestoneSchema,
  VideoPlaySchema,
  ViewCartSchema,
  ViewItemListSchema,
  ViewItemSchema,
  ViewProductPageSchema,
  ViewPromotionSchema,
} from './schema';
import type {
  AllAnalyticsEvent,
  AllEventHandler,
  AllEventMethods,
  AnalyticsSink,
} from './types';

const safeCheckEnabled = (sink: AnalyticsSink): boolean => {
  try {
    return sink.checkEnabled();
  } catch (err) {
    captureException(err as Error);
    return false;
  }
};

export class AnalyticsDelegator implements AllEventMethods {
  private sinks: Map<string, AnalyticsSink>;

  private scriptManager?: ScriptManager;

  private scriptManagerUnsubscribe?: () => void;

  constructor() {
    this.sinks = new Map();
  }

  registerScriptManager(scriptManager: ScriptManager) {
    if (this.scriptManager === scriptManager) return;

    this.scriptManagerUnsubscribe?.();
    this.scriptManager = scriptManager;
    /* Subscribe once per script manager. The callback iterates the
     * current sinks at notify time, so sinks registered later are
     * still picked up. */
    this.scriptManagerUnsubscribe = scriptManager.subscribe(() => {
      this.sinks.forEach((s) => {
        safeCheckEnabled(s);
      });
    });
  }

  registerSink(sink: AnalyticsSink) {
    this.sinks.set(sink.id, sink);
  }

  removeSink(sink: AnalyticsSink) {
    this.sinks.delete(sink.id);
  }

  getSink<T extends AnalyticsSink>(id: AnalyticsID) {
    return this.sinks.get(id) as T | undefined;
  }

  private safeRun = safeRun((error) => {
    captureException(error);
  });

  private dispatch<T extends AllAnalyticsEvent['type']>(
    eventType: T,
    payload: Parameters<AllEventHandler<T>>[0],
  ) {
    this.sinks.forEach((sink) => {
      try {
        const handler = sink.events[eventType];
        if (safeCheckEnabled(sink) && handler && sink.isReady()) {
          this.safeRun(handler, payload);
        }
      } catch (err) {
        captureException(err as Error);
      }
    });
  }

  isSinkEnabled(id: string) {
    const sink = this.sinks.get(id);
    return sink ? safeCheckEnabled(sink) : false;
  }

  pageView(payload: PageViewSchema) {
    this.dispatch('pageView', payload);
  }

  cookiesDisplay() {
    this.dispatch('cookiesDisplay', undefined);
  }

  cookiesAccept() {
    this.dispatch('cookiesAccept', undefined);
  }

  cookiesDecline() {
    this.dispatch('cookiesDecline', undefined);
  }

  beginCheckout(payload: BeginCheckoutSchema) {
    this.dispatch('beginCheckout', payload);
  }

  cartUpsellDisplay(payload: CartUpsellSchema) {
    this.dispatch('cartUpsellDisplay', payload);
  }

  cartUpsellAccepted(payload: CartUpsellSchema) {
    this.dispatch('cartUpsellAccepted', payload);
  }

  login(payload: LoginSchema) {
    this.dispatch('login', payload);
  }

  selectItem(payload: SelectItemSchema) {
    this.dispatch('selectItem', payload);
  }

  selectPromotion(payload: SelectPromotionSchema) {
    this.dispatch('selectPromotion', payload);
  }

  viewItemList(payload: ViewItemListSchema) {
    this.dispatch('viewItemList', payload);
  }

  viewPromotion(payload: ViewPromotionSchema) {
    this.dispatch('viewPromotion', payload);
  }

  addToCart(payload: CartUpdateSchema) {
    this.dispatch('addToCart', payload);
  }

  removeFromCart(payload: CartUpdateSchema) {
    this.dispatch('removeFromCart', payload);
  }

  viewItem(payload: ViewItemSchema) {
    this.dispatch('viewItem', payload);
  }

  viewCart(payload: ViewCartSchema) {
    this.dispatch('viewCart', payload);
  }

  viewProductPage(payload: ViewProductPageSchema) {
    this.dispatch('viewProductPage', payload);
  }

  searchClick(payload: SearchClickSchema) {
    this.dispatch('searchClick', payload);
  }

  searchQuery(payload: SearchQuerySchema) {
    this.dispatch('searchQuery', payload);
  }

  aboutVideoPlay() {
    this.dispatch('aboutVideoPlay', undefined);
  }

  overlayPopupDisplay() {
    this.dispatch('overlayPopupDisplay', undefined);
  }

  overlayPopupClick(payload: OverlayPopupClickSchema) {
    this.dispatch('overlayPopupClick', payload);
  }

  overlayPopupClose() {
    this.dispatch('overlayPopupClose', undefined);
  }

  quizStart() {
    this.dispatch('quizStart', undefined);
  }

  quizComplete() {
    this.dispatch('quizComplete', undefined);
  }

  quizAnswer(payload: QuizAnswerSchema) {
    this.dispatch('quizAnswer', payload);
  }

  quizResults(payload: QuizResultsSchema) {
    this.dispatch('quizResults', payload);
  }

  quizResultsBundle() {
    this.dispatch('quizResultsBundle', undefined);
  }

  quizResultsATC(payload: QuizResultsATCSchema) {
    this.dispatch('quizResultsATC', payload);
  }

  quizResultsShare() {
    this.dispatch('quizResultsShare', undefined);
  }

  siteToggleSkincareClick() {
    this.dispatch('siteToggleSkincareClick', undefined);
  }

  siteToggleSupplementsClick() {
    this.dispatch('siteToggleSupplementsClick', undefined);
  }

  siteToggleCalloutDisplay() {
    this.dispatch('siteToggleCalloutDisplay', undefined);
  }

  siteToggleCalloutClose() {
    this.dispatch('siteToggleCalloutClose', undefined);
  }

  siteToggleCalloutClick() {
    this.dispatch('siteToggleCalloutClick', undefined);
  }

  skincareCardDisplay(payload: SkincareCardDisplaySchema) {
    this.dispatch('skincareCardDisplay', payload);
  }

  skincareCardClick(payload: SkincareCardClickSchema) {
    this.dispatch('skincareCardClick', payload);
  }

  popupBannerDisplay(payload: PopupBannerSchema) {
    this.dispatch('popupBannerDisplay', payload);
  }

  popupBannerClick(payload: PopupBannerSchema) {
    this.dispatch('popupBannerClick', payload);
  }

  popupBannerClose(payload: PopupBannerSchema) {
    this.dispatch('popupBannerClose', payload);
  }

  freegiftShowProgressBar() {
    this.dispatch('freegiftShowProgressBar', undefined);
  }

  freegiftShowChoices(payload: FreegiftProductSchema) {
    this.dispatch('freegiftShowChoices', payload);
  }

  freegiftAccept(payload: FreegiftProductSchema) {
    this.dispatch('freegiftAccept', payload);
  }

  longevityMonthView(payload: LongevityMonthViewSchema) {
    this.dispatch('longevityMonthView', payload);
  }

  longevityMonthPartnershipClick(
    payload: LongevityMonthPartnershipClickSchema,
  ) {
    this.dispatch('longevityMonthPartnershipClick', payload);
  }

  longevityMonthShareInsta(payload: LongevityMonthShareInstaSchema) {
    this.dispatch('longevityMonthShareInsta', payload);
  }

  longevityMonthShareTiktok(payload: LongevityMonthShareTiktokSchema) {
    this.dispatch('longevityMonthShareTiktok', payload);
  }

  longevityMonthFollowInsta(payload: LongevityMonthFollowInstaSchema) {
    this.dispatch('longevityMonthFollowInsta', payload);
  }

  skincareHeroBadgeClick() {
    this.dispatch('skincareHeroBadgeClick', undefined);
  }

  videoPlay(payload: VideoPlaySchema) {
    this.dispatch('videoPlay', payload);
  }

  videoMilestone(payload: VideoMilestoneSchema) {
    this.dispatch('videoMilestone', payload);
  }

  hcpLinkClick(payload: HcpLinkClickSchema) {
    this.dispatch('hcpLinkClick', payload);
  }
}
